// Maintained in sanity-nextjs-boilerplate. Edit there and run sync-to-projects.sh,
// or lift downstream improvements with sync-from-project.sh.
//
// Single source of truth for "is this editor-supplied href safe to render".
// Previously this allow-list existed as a copy-pasted regex in both
// portable-text-body.tsx and resolveLink.ts; they drifted in their comments and
// shared the same hole (#245).
//
// WHY THIS IS NOT A PREFIX TEST. The old form was:
//
//   /^(https?:\/\/|mailto:|tel:|#\S|\/(?!\/))/i
//
// `\/(?!\/)` means "a slash not followed by a slash", which is narrower than
// what a browser treats as protocol-relative. Four inputs passed it and still
// resolved off-origin, verified against Node's WHATWG URL parser:
//
//   /\evil.com        -> https://evil.com
//   /<TAB>/evil.com   -> https://evil.com
//   /<LF>/evil.com    -> https://evil.com
//   /<CR>/evil.com    -> https://evil.com
//
// Two parser behaviours cause it. Backslash is equivalent to slash in the
// path-start state for special schemes (http/https), which is why Django and
// ASP.NET special-case it too. And ASCII tab, LF and CR are stripped from
// ANYWHERE in the URL before parsing, not just the ends — so `trim()` does not
// help and the regex sees a character that the parser will delete.
//
// The same class already bit us once in `safeRedirectPath` (#137,
// `?redirect=%2F%5Cevil.com`). Canon'S conclusion from that incident was to
// stop hand-enumerating hostile prefixes and instead resolve against a base and
// assert the origin. This applies it.
//
// RETURNS THE SANITISED STRING, NOT A BOOLEAN — deliberately. Validating a
// stripped copy while returning the caller's original re-opens the hole: the
// browser strips tab/LF/CR too, so `/<TAB>/evil.com` would validate as the
// harmless `//evil.com`-free string and then navigate off-origin anyway. The
// value that was checked must be the value that is used.

/** Characters the URL parser removes from anywhere in the input before parsing. */
const URL_IGNORED = /[\t\n\r]/g

/**
 * Characters the URL parser removes from the ENDS of the input — every C0
 * control plus space (WHATWG URL, "Remove any leading and trailing C0 control
 * or space from input").
 *
 * Same reasoning as `URL_IGNORED`, applied to the other half of the parser's
 * normalisation. Without it the guard disagrees with the parser in both
 * directions: a pasted `" https://doi.org/..."` was rejected outright, while a
 * trailing space survived into the rendered `href` unsanitised — so the value
 * checked was not quite the value used, which is the thing this module exists
 * to prevent.
 *
 * Editors paste padded URLs constantly; one such link was rendering as plain
 * text on distriktssenteret (`/naturverdiar-i-din-kommune`, a doi.org link with
 * a leading space). Trimming here rather than at the call site keeps the
 * checked string and the rendered string identical.
 *
 * This does NOT weaken the guard: the padded forms of hostile input
 * (`" /\\evil.com"`, `" //evil.com"`) still fail, because trimming only
 * restores what the parser itself would have seen anyway.
 */
const URL_EDGE_TRIMMED = /^[\u0000-\u0020]+|[\u0000-\u0020]+$/g

/** Schemes safe to hand to an anchor. Deliberately an allow-list. */
const SAFE_SCHEME = /^(https?:\/\/|mailto:|tel:)/i

// Any host would do; `.invalid` is reserved by RFC 2606 so it can never resolve.
const BASE = 'https://safe-href.invalid'
const BASE_ORIGIN = new URL(BASE).origin

/**
 * Returns the href to render, or `null` when it is not safe.
 *
 * Callers substitute their own sentinel for `null` — `resolveHref` uses `'#'`,
 * the PortableText renderer drops the anchor. Pass the value AFTER `stegaClean`:
 * cleaning can turn an apparently-safe string into a protocol-relative one, so
 * clean first, then validate, then render exactly what came back from here.
 */
export function safeHref(raw: string | undefined | null): string | null {
  if (!raw) return null

  const value = raw.replace(URL_IGNORED, '').replace(URL_EDGE_TRIMMED, '')
  if (!value) return null

  if (SAFE_SCHEME.test(value)) return value

  // Same-page anchor. `#` alone is not a destination.
  if (value.startsWith('#')) return value.length > 1 ? value : null

  // Everything else must be a root-relative path. A leading backslash is not a
  // path in any authoring sense; it only appears in attempts to exploit the
  // path-start equivalence above.
  if (!value.startsWith('/')) return null

  try {
    return new URL(value, BASE).origin === BASE_ORIGIN ? value : null
  } catch {
    return null
  }
}
